import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import { api, resolveUrl, formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { differenceInDays, format, parseISO } from "date-fns";
import { fmtDate } from "../lib/dateFormat";
import { ArrowLeft, Check, Star, Scale, ZoomIn, PenLine } from "lucide-react";
import SeoHead from "../components/SeoHead";
import PublicReviewModal from "../components/PublicReviewModal";
import { buildWhatsappUrl, getWhatsappMessage } from "../lib/whatsapp";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { useComparison } from "../contexts/ComparisonContext";

export default function CarDetails() {
  const { id } = useParams();
  const { t, lang, currency } = useI18n();
  const { settings } = useSettings();
  const [car, setCar] = useState(null);
  const [activeMedia, setActiveMedia] = useState({ type: "image", idx: 0 });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { has: hasCompare, add: addCompare, remove: removeCompare, canAddMore } = useComparison();
  const [availability, setAvailability] = useState({ reserved_intervals: [], blocked_intervals: [] });
  const [reviews, setReviews] = useState([]);
  const [range, setRange] = useState();
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", message: "", coupon_code: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponMsg, setCouponMsg] = useState("");
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [customFields, setCustomFields] = useState({});

  useEffect(() => {
    api.get(`/cars/${id}`).then((r) => setCar(r.data));
    api.get(`/cars/${id}/availability`).then((r) => setAvailability(r.data));
    api.get(`/cars/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  const [priceBreakdown, setPriceBreakdown] = useState(null);
  useEffect(() => {
    if (!range?.from || !range?.to) { setPriceBreakdown(null); return; }
    api.post(`/cars/${id}/calculate-price`, {
      start_date: format(range.from, "yyyy-MM-dd"),
      end_date: format(range.to, "yyyy-MM-dd"),
    }).then((r) => setPriceBreakdown(r.data)).catch(() => setPriceBreakdown(null));
  }, [range, id]);

  const disabledIntervals = useMemo(() => {
    return [...availability.reserved_intervals, ...availability.blocked_intervals]
      .map((i) => ({ from: parseISO(i.start_date), to: parseISO(i.end_date) }));
  }, [availability]);

  const days = priceBreakdown?.days || (range?.from && range?.to ? differenceInDays(range.to, range.from) + 1 : 0);
  const rate = settings?.exchange_rate_eur_to_ron || 4.97;
  const baseEUR = priceBreakdown?.subtotal ?? (car ? days * car.price_eur : 0);
  const durationDiscountEUR = priceBreakdown?.discount_amount || 0;
  let couponDiscountEUR = 0;
  const afterDuration = Math.max(0, baseEUR - durationDiscountEUR);
  if (couponInfo) {
    if (couponInfo.discount_pct) couponDiscountEUR = afterDuration * (couponInfo.discount_pct / 100);
    if (couponInfo.discount_amount_eur) couponDiscountEUR += couponInfo.discount_amount_eur;
  }
  const totalEUR = Math.max(0, afterDuration - couponDiscountEUR);
  const total = currency === "EUR" ? `€${Math.round(totalEUR)}` : `${Math.round(totalEUR * rate)} RON`;

  const applyCoupon = async () => {
    setCouponMsg("");
    if (!form.coupon_code) { setCouponInfo(null); return; }
    try {
      const r = await api.post("/coupons/validate", { code: form.coupon_code });
      setCouponInfo(r.data); setCouponMsg("Cupon aplicat");
    } catch (e) { setCouponInfo(null); setCouponMsg(formatApiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) { setError(t("car.pickDates")); return; }
    setSubmitting(true); setError("");
    try {
      await api.post("/reservations", {
        car_id: id, guest_name: form.guest_name, guest_email: form.guest_email,
        guest_phone: form.guest_phone, message: form.message,
        coupon_code: couponInfo ? form.coupon_code : null,
        start_date: format(range.from, "yyyy-MM-dd"), end_date: format(range.to, "yyyy-MM-dd"),
        lang, custom_fields: customFields,
      });
      setSuccess(true);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (!car) {
    return (
      <div>
        <SeoHead title={settings?.brand_name || "Inchirieri Auto Pascani"} description="" />
        <PublicHeader />
        <div className="pt-40 text-center" style={{ color: "var(--brand-text-secondary)" }}>
          <div className="scroll-line mx-auto mb-6" />
          <span className="label-eyebrow">{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  const description = lang === "ro" ? car.description_ro : car.description_en;
  const priceDisplay = currency === "EUR" ? `€${car.price_eur}` : `${Math.round(car.price_eur * rate)} RON`;
  const heroImg = car.images?.[0]?.url ? resolveUrl(car.images[0].url) : undefined;
  const brandName = settings?.brand_name || "Inchirieri Auto Pascani";
  const seoTitle = `${car.name} — ${brandName}`;
  const seoDescription = (description || "").slice(0, 160) || `${car.name} disponibil pentru inchiriere in Pascani.`;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Vehicle",
    name: car.name, brand: { "@type": "Brand", name: car.brand || "" },
    vehicleTransmission: car.transmission || "", image: heroImg, description: description || "",
    offers: { "@type": "Offer", price: car.price_eur, priceCurrency: "EUR", availability: "https://schema.org/InStock", url: typeof window !== "undefined" ? window.location.href : "" },
  };

  const allImages = car.images || [];
  const allVideos = car.videos || [];
  const mediaCount = allImages.length + allVideos.length;
  const waEnabled = settings?.contact?.phone && (settings?.floating_contact?.whatsapp_enabled !== false);

  return (
    <div>
      <SeoHead title={seoTitle} description={seoDescription} image={heroImg} jsonLd={jsonLd} jsonLdId={`ld-car-${id}`} />
      <PublicHeader />

      <main data-testid="car-details-page">

        {/* ── GALLERY + INFO ── */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12">

          {/* Back + breadcrumb */}
          <div className="pt-28 sm:pt-32 pb-8 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest transition-colors hover:text-[var(--brand-primary)]" style={{ color: "var(--brand-text-secondary)" }} data-testid="back-link">
              <ArrowLeft className="w-3.5 h-3.5" />{t("common.back")}
            </Link>
            <div className="label-eyebrow" style={{ color: "var(--brand-text-secondary)" }}>
              {car.brand}{car.year ? ` · ${car.year}` : ""}
            </div>
          </div>

          {/* Car name */}
          <h1 className="font-serif font-light leading-none tracking-tight mb-10" style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }} data-testid="car-name">
            {car.name}
          </h1>

          {/* Gallery + Info grid */}
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 mb-20">

            {/* Gallery */}
            <div className="lg:col-span-7 min-w-0">
              {/* Main media */}
              <div className="overflow-hidden relative w-full" style={{ aspectRatio: "16/10", background: "var(--brand-surface-elevated)" }}>
                {(() => {
                  const isVideo = activeMedia.type === "video";
                  const items = isVideo ? allVideos : allImages;
                  const item = items[activeMedia.idx];
                  if (!item) return <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "var(--brand-text-secondary)" }}>Fara media</div>;
                  if (isVideo) return <video key={`${item.id}-${activeMedia.idx}`} src={resolveUrl(item.url)} poster={item.thumbnail_url ? resolveUrl(item.thumbnail_url) : undefined} className="w-full h-full object-cover" controls autoPlay muted playsInline loop preload="metadata" data-testid="main-video" />;
                  return (
                    <button type="button" onClick={() => { setLightboxIndex(activeMedia.idx); setLightboxOpen(true); }} className="w-full h-full block relative group cursor-zoom-in" data-testid="main-image-zoom" aria-label={lang === "en" ? "Open gallery" : "Deschide galeria"}>
                      <img src={resolveUrl(item.url)} alt={car.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" data-testid="main-image" />
                      <div className="absolute bottom-4 right-4 backdrop-blur-sm px-3 py-1.5 flex items-center gap-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ background: "rgba(5,5,5,0.8)", border: "1px solid rgba(255,255,255,0.1)" }}>
                        <ZoomIn className="w-3.5 h-3.5" style={{ color: "var(--brand-primary)" }} />
                        <span style={{ color: "rgba(255,255,255,0.8)" }}>{lang === "en" ? "Open gallery" : "Galerie"}</span>
                      </div>
                    </button>
                  );
                })()}
                {allImages.length > 0 && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); if (hasCompare(car.id)) removeCompare(car.id); else if (canAddMore) addCompare(car.id); }} className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest backdrop-blur-sm transition-all" style={{ background: hasCompare(car.id) ? "var(--brand-primary)" : "rgba(0,0,0,0.65)", color: hasCompare(car.id) ? "#0A0A0A" : "var(--brand-text)", border: "1px solid " + (hasCompare(car.id) ? "var(--brand-primary)" : "rgba(255,255,255,0.12)") }} data-testid={`compare-toggle-details-${id}`}>
                    {hasCompare(car.id) ? <Check className="w-3 h-3" /> : <Scale className="w-3 h-3" />}
                    {hasCompare(car.id) ? (lang === "en" ? "Added" : "Adaugat") : (lang === "en" ? "Compare" : "Compara")}
                  </button>
                )}
              </div>

              {/* Film-strip thumbnails */}
              {mediaCount > 1 && (
                <div className="overflow-hidden pt-3">
                  <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-testid="gallery-thumbs">
                    {allImages.map((img, idx) => (
                      <button key={img.id} onClick={() => setActiveMedia({ type: "image", idx })} onDoubleClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }} className="shrink-0 overflow-hidden transition-all duration-200" style={{ width: 72, aspectRatio: "4/3", opacity: activeMedia.type === "image" && activeMedia.idx === idx ? 1 : 0.5, boxShadow: activeMedia.type === "image" && activeMedia.idx === idx ? "0 0 0 2px var(--brand-primary)" : "none" }} data-testid={`thumb-img-${idx}`}>
                        <img src={resolveUrl(img.url)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                    {allVideos.map((vid, idx) => (
                      <button key={vid.id} onClick={() => setActiveMedia({ type: "video", idx })} className="shrink-0 overflow-hidden relative transition-all duration-200" style={{ width: 72, aspectRatio: "4/3", opacity: activeMedia.type === "video" && activeMedia.idx === idx ? 1 : 0.5, boxShadow: activeMedia.type === "video" && activeMedia.idx === idx ? "0 0 0 2px var(--brand-primary)" : "none" }} data-testid={`thumb-vid-${idx}`}>
                        {vid.thumbnail_url ? <img src={resolveUrl(vid.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: "var(--brand-surface-elevated)" }} />}
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }}><span style={{ color: "var(--brand-primary)", fontSize: 16 }}>▶</span></div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Info panel */}
            <div className="lg:col-span-5 min-w-0 flex flex-col">
              <div className="mb-8">
                <div className="label-eyebrow mb-2">{t("common.from")}</div>
                <div className="flex items-baseline gap-3">
                  <span className="font-serif" style={{ fontSize: "clamp(2.5rem, 4vw, 3.5rem)", color: "var(--brand-primary)", lineHeight: 1 }}>{priceDisplay}</span>
                  <span className="label-eyebrow" style={{ color: "var(--brand-text-secondary)" }}>{t("common.perDay")}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 mb-8" style={{ border: "1px solid var(--brand-border)", gap: 1, background: "var(--brand-border)" }}>
                <SpecCell label={t("common.transmission")} value={t(`common.${car.transmission}`)} />
                <SpecCell label={t("common.fuel")} value={t(`common.${car.fuel}`)} />
                <SpecCell label={t("common.seats")} value={car.seats} />
                {car.year && <SpecCell label={t("common.year")} value={car.year} />}
                {car.engine_cc && <SpecCell label={lang === "en" ? "Engine" : "Motor"} value={`${car.engine_cc} cc`} />}
                {car.horsepower && <SpecCell label={lang === "en" ? "Power" : "Putere"} value={`${car.horsepower} CP`} />}
                {car.doors && <SpecCell label={lang === "en" ? "Doors" : "Usi"} value={car.doors} />}
                {car.luggage_count != null && <SpecCell label={lang === "en" ? "Luggage" : "Bagaje"} value={car.luggage_count} />}
              </div>

              {description && (
                <div className="mb-8">
                  <div className="label-eyebrow mb-3">{t("common.description")}</div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--brand-text-secondary)" }}>{description}</p>
                </div>
              )}

              {car.features?.length > 0 && (
                <div className="mb-8">
                  <div className="label-eyebrow mb-4">{t("common.features")}</div>
                  <ul className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-sm">
                    {car.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: "var(--brand-primary)" }} />
                        <span style={{ color: "var(--brand-text-secondary)" }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-auto">
                <ShareRow car={car} lang={lang} settings={settings} />
              </div>
            </div>
          </div>
        </div>

        {/* ── RESERVATION ── */}
        <section
          style={{ background: "var(--brand-surface)", borderTop: "1px solid var(--brand-border)", borderBottom: "1px solid var(--brand-border)" }}
          data-testid="reservation-block"
        >
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24">

            {/* Header */}
            <div className="mb-12 sm:mb-16">
              <div className="label-eyebrow mb-3" style={{ color: "var(--brand-primary)" }}>{t("car.selectDates")}</div>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8">
                <h2 className="font-serif font-light" style={{ fontSize: "clamp(1.8rem, 3.5vw, 3rem)" }}>{t("car.reserveTitle")}</h2>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-2xl" style={{ color: "var(--brand-primary)" }}>{priceDisplay}</span>
                  <span className="label-eyebrow" style={{ color: "var(--brand-text-secondary)" }}>{t("common.perDay")}</span>
                </div>
              </div>
            </div>

            {success ? (
              <div className="text-center py-16" data-testid="success-message">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid var(--brand-primary)" }}>
                  <Check className="w-6 h-6" style={{ color: "var(--brand-primary)" }} />
                </div>
                <h3 className="font-serif text-3xl mb-4">{t("car.successTitle")}</h3>
                <p className="text-sm max-w-md mx-auto mb-8" style={{ color: "var(--brand-text-secondary)" }}>{t("car.successBody")}</p>
                <Link to="/" className="btn-gold">← {t("common.back")}</Link>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">

                {/* Calendar */}
                <div>
                  <div data-testid="date-picker">
                    <DayPicker
                      mode="range"
                      selected={range}
                      onSelect={setRange}
                      numberOfMonths={1}
                      disabled={[{ before: new Date() }, ...disabledIntervals]}
                    />
                  </div>
                  {range?.from && range?.to && (
                    <div className="mt-6 pt-6 flex items-center justify-between" style={{ borderTop: "1px solid var(--brand-border)" }}>
                      <div>
                        <div className="label-eyebrow mb-1.5" style={{ color: "var(--brand-text-secondary)" }}>
                          {lang === "en" ? "Period" : "Perioada"}
                        </div>
                        <div className="text-sm">{fmtDate(format(range.from, "yyyy-MM-dd"))} → {fmtDate(format(range.to, "yyyy-MM-dd"))}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-serif leading-none" style={{ fontSize: "3rem", color: "var(--brand-primary)" }}>{days}</div>
                        <div className="label-eyebrow mt-1" style={{ color: "var(--brand-text-secondary)" }}>{lang === "en" ? "days" : "zile"}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Form */}
                <form onSubmit={submit} className="flex flex-col gap-6" data-testid="reservation-form">
                  <input required placeholder={t("car.yourName")} value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="input-line" data-testid="input-name" />
                  <input required type="email" placeholder={t("car.yourEmail")} value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className="input-line" data-testid="input-email" />
                  <input required placeholder={t("car.yourPhone")} value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="input-line" data-testid="input-phone" />
                  <textarea rows={3} placeholder={t("car.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-line resize-none" data-testid="input-message" />

                  {(settings?.reservation_fields || []).filter((f) => f.enabled !== false).map((f) => (
                    <div key={f.key}>
                      {f.type === "textarea"
                        ? <textarea rows={2} className="input-line resize-none" placeholder={`${lang === "en" ? f.label_en : f.label_ro}${f.required ? " *" : ""}`} required={f.required} value={customFields[f.key] || ""} onChange={(e) => setCustomFields({ ...customFields, [f.key]: e.target.value })} data-testid={`custom-${f.key}`} />
                        : <input className="input-line" placeholder={`${lang === "en" ? f.label_en : f.label_ro}${f.required ? " *" : ""}`} required={f.required} value={customFields[f.key] || ""} onChange={(e) => setCustomFields({ ...customFields, [f.key]: e.target.value })} data-testid={`custom-${f.key}`} />}
                    </div>
                  ))}

                  {/* Coupon row */}
                  <div className="flex items-end gap-4" style={{ borderBottom: "1px solid var(--brand-border)" }}>
                    <input placeholder="Cod reducere (optional)" value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })} className="input-line flex-1" style={{ borderBottom: "none" }} data-testid="input-coupon" />
                    <button type="button" onClick={applyCoupon} className="label-eyebrow pb-[0.875rem] shrink-0 transition-colors hover:text-[var(--brand-primary)]" style={{ color: "var(--brand-text-secondary)" }} data-testid="apply-coupon">
                      {lang === "en" ? "Apply" : "Aplica"}
                    </button>
                  </div>
                  {couponMsg && <div className="text-xs -mt-4" style={{ color: couponInfo ? "var(--brand-primary)" : "#ef4444" }} data-testid="coupon-msg">{couponMsg}</div>}

                  {/* Price breakdown */}
                  {range?.from && range?.to && (
                    <div className="py-5 border-t border-b space-y-2.5" style={{ borderColor: "var(--brand-border)" }} data-testid="total-display">
                      <div className="flex justify-between text-xs" style={{ color: "var(--brand-text-secondary)" }}>
                        <span>Subtotal · {days} {days === 1 ? "zi" : "zile"}</span>
                        <span>€{Math.round(baseEUR)}</span>
                      </div>
                      {durationDiscountEUR > 0 && (
                        <div className="flex justify-between text-xs" style={{ color: "var(--brand-primary)" }}>
                          <span>Reducere durata ({priceBreakdown?.discount_pct}%)</span>
                          <span>-€{Math.round(durationDiscountEUR)}</span>
                        </div>
                      )}
                      {couponInfo && couponDiscountEUR > 0 && (
                        <div className="flex justify-between text-xs" style={{ color: "var(--brand-primary)" }}>
                          <span>Cupon ({couponInfo.code})</span>
                          <span>-€{Math.round(couponDiscountEUR)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-baseline pt-4 mt-1 border-t" style={{ borderColor: "var(--brand-border)" }}>
                        <span className="label-eyebrow">{t("car.total")}</span>
                        <span className="font-serif leading-none" style={{ fontSize: "2.5rem", color: "var(--brand-primary)" }}>{total}</span>
                      </div>
                    </div>
                  )}

                  {error && <div className="text-sm" style={{ color: "#ef4444" }} data-testid="form-error">{error}</div>}

                  <button type="submit" disabled={submitting} className="btn-gold-filled w-full" data-testid="submit-reservation">
                    {submitting ? t("car.submitting") : t("car.submit")}
                  </button>

                  {waEnabled && (
                    <a
                      href={buildWhatsappUrl({ phone: settings.contact.phone, message: getWhatsappMessage({ settings, lang, kind: range?.from && range?.to ? "detailed" : "short", vars: { car_name: car ? `${car.brand} ${car.model}` : "", pickup: range?.from ? format(range.from, "dd.MM.yyyy") : "", return: range?.to ? format(range.to, "dd.MM.yyyy") : "", days: days || "", total: total || "", site: window.location.origin } }) })}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 px-5 text-sm font-medium transition-transform hover:scale-[1.01]"
                      style={{ background: "#25D366", color: "#fff", borderRadius: 999 }}
                      data-testid="car-whatsapp-btn"
                    >
                      <svg width="18" height="18" viewBox="0 0 32 32" fill="currentColor" aria-hidden>
                        <path d="M19.11 17.32c-.27-.13-1.59-.78-1.83-.87-.25-.09-.43-.13-.61.13-.18.27-.7.87-.86 1.05-.16.18-.32.2-.59.07-.27-.13-1.14-.42-2.17-1.34-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.47-.07-.13-.61-1.47-.83-2.01-.22-.53-.45-.45-.61-.46-.16-.01-.34-.01-.52-.01-.18 0-.47.07-.72.34-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.1 2.81.13.18 1.9 2.9 4.61 4.07.64.28 1.15.45 1.54.57.65.21 1.24.18 1.71.11.52-.08 1.59-.65 1.81-1.28.22-.62.22-1.16.16-1.28-.07-.13-.25-.2-.52-.34zM16.02 4C9.39 4 4 9.39 4 16.02c0 2.12.55 4.18 1.6 6L4 28l6.13-1.6a11.95 11.95 0 0 0 5.89 1.5h.01c6.63 0 12.02-5.39 12.02-12.02C28.05 9.39 22.66 4 16.02 4zm0 21.94c-1.86 0-3.69-.5-5.29-1.45l-.38-.22-3.65.96.97-3.55-.25-.39a9.94 9.94 0 0 1-1.55-5.33c0-5.49 4.47-9.96 9.96-9.96s9.96 4.47 9.96 9.96-4.47 9.98-9.97 9.98z" />
                      </svg>
                      <span>{lang === "en" ? "Ask on WhatsApp" : "Intreaba pe WhatsApp"}</span>
                    </a>
                  )}
                </form>
              </div>
            )}
          </div>
        </section>

        {/* ── REVIEWS ── */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 py-20 sm:py-28" data-testid="reviews-section">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="label-eyebrow mb-3" style={{ color: "var(--brand-primary)" }}>Experiente reale</div>
              <h2 className="font-serif font-light text-3xl sm:text-4xl">Recenzii clienti</h2>
            </div>
            <button onClick={() => setOpenReviewModal(true)} className="btn-gold inline-flex items-center gap-2" data-testid="car-write-review-btn">
              <PenLine className="w-3.5 h-3.5" /> Scrie o recenzie
            </button>
          </div>

          {reviews.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--brand-border)" }}>
              {reviews.map((rv) => (
                <div key={rv.id} className="p-6" style={{ background: "var(--brand-surface)" }} data-testid={`public-review-${rv.id}`}>
                  <div className="flex items-center gap-1 mb-4">
                    {[1,2,3,4,5].map((n) => <Star key={n} className="w-3.5 h-3.5" fill={n <= rv.rating ? "var(--brand-primary)" : "none"} stroke="var(--brand-primary)" />)}
                  </div>
                  {rv.comment && <p className="text-sm leading-relaxed mb-4 italic" style={{ color: "var(--brand-text-secondary)" }}>"{rv.comment}"</p>}
                  {rv.media?.length > 0 && (
                    <div className="grid grid-cols-3 gap-1 mb-4">
                      {rv.media.slice(0, 3).map((m) => (
                        <div key={m.id} className="aspect-square overflow-hidden">
                          {m.type === "image" ? <img src={resolveUrl(m.url)} alt="" className="w-full h-full object-cover" /> : <video src={resolveUrl(m.url)} className="w-full h-full object-cover" controls />}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="label-eyebrow text-[9px]" style={{ color: "var(--brand-primary)" }}>{rv.guest_name}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-sm" style={{ color: "var(--brand-text-secondary)", border: "1px solid var(--brand-border)" }} data-testid="reviews-empty">
              Inca nu exista recenzii. Fii primul!
            </div>
          )}
        </div>
      </main>

      <PublicReviewModal open={openReviewModal} onClose={() => setOpenReviewModal(false)} targetType="car" carId={id} carName={car?.name} />
      <PublicFooter />

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={allImages.map((img, i) => ({ src: resolveUrl(img.url), alt: `${car.name} - foto ${i + 1}`, title: car.name, description: car.brand ? `${car.brand} ${car.year || ""}`.trim() : "" }))}
        plugins={[Zoom, Counter, Captions, Thumbnails, Fullscreen, Slideshow]}
        counter={{ container: { style: { top: "unset", bottom: 16, left: 16 } } }}
        carousel={{ finite: false, padding: "0px", spacing: "16px" }}
        thumbnails={{ position: "bottom", width: 80, height: 60, borderRadius: 0, padding: 2, gap: 8 }}
        slideshow={{ autoplay: false, delay: 3500 }}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true, wheelZoomDistanceFactor: 100, pinchZoomDistanceFactor: 100, doubleTapDelay: 300, doubleClickDelay: 300, doubleClickMaxStops: 2, keyboardMoveDistance: 50 }}
        animation={{ swipe: 250, fade: 250 }}
        controller={{ closeOnBackdropClick: true, closeOnPullDown: true }}
        styles={{ container: { backgroundColor: "rgba(5,5,5,0.97)" }, thumbnail: { backgroundColor: "rgba(20,20,20,0.6)" }, thumbnailsContainer: { backgroundColor: "rgba(0,0,0,0.5)" } }}
      />
    </div>
  );
}

function SpecCell({ label, value }) {
  return (
    <div className="px-4 py-3" style={{ background: "var(--brand-surface)" }} data-testid={`spec-${label}`}>
      <div className="label-eyebrow mb-1 text-[9px]">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function ShareRow({ car, lang, settings }) {
  const brandName = settings?.brand_name || "Inchirieri Auto Pascani";
  const template = (lang === "en" ? settings?.seo?.social_share_template_en : settings?.seo?.social_share_template_ro)
    || (lang === "en" ? "{car_name} • from €{price}/day • {brand_name}" : "{car_name} • de la €{price}/zi • {brand_name}");
  const customDesc = lang === "en" ? car.social_share_en : car.social_share_ro;
  const description = customDesc || template
    .replaceAll("{car_name}", car.name || "")
    .replaceAll("{price}", String(car.price_eur || ""))
    .replaceAll("{brand_name}", brandName)
    .replaceAll("{brand}", car.brand || "");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const ogShareUrl = `${origin}/api/og/cars/${car.id}`;
  const enc = encodeURIComponent;

  const links = [
    { key: "whatsapp", label: "WhatsApp", color: "#25D366", href: `https://wa.me/?text=${enc(description + " — " + ogShareUrl)}` },
    { key: "facebook", label: "Facebook", color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(ogShareUrl)}` },
    { key: "telegram", label: "Telegram", color: "#26A5E4", href: `https://t.me/share/url?url=${enc(ogShareUrl)}&text=${enc(description)}` },
    { key: "email", label: "Email", color: "#555", href: `mailto:?subject=${enc(car.name)}&body=${enc(description + "\n\n" + ogShareUrl)}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ogShareUrl);
      const el = document.getElementById(`copied-${car.id}`);
      if (el) { el.style.opacity = "1"; setTimeout(() => (el.style.opacity = "0"), 1500); }
    } catch { window.prompt("Link:", ogShareUrl); }
  };

  return (
    <div data-testid="share-row">
      <div className="label-eyebrow mb-3">{lang === "en" ? "Share" : "Distribuie"}</div>
      <div className="flex flex-wrap gap-2 items-center">
        {links.map((l) => (
          <a key={l.key} href={l.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 transition hover:opacity-90" style={{ background: l.color, color: "#fff", borderRadius: 999 }} data-testid={`share-${l.key}`}>{l.label}</a>
        ))}
        <button onClick={copy} className="btn-ghost text-xs px-3 py-1.5" style={{ borderRadius: 999 }} data-testid="share-copy">{lang === "en" ? "Copy link" : "Copiaza link"}</button>
        <span id={`copied-${car.id}`} className="text-xs transition-opacity duration-300" style={{ color: "var(--brand-primary)", opacity: 0 }}>{lang === "en" ? "Copied!" : "Copiat!"}</span>
      </div>
    </div>
  );
}
